import dayjs, { Dayjs } from 'dayjs';

/**
 * 最小データモデル（UIモック用）
 * - ISODate: 日単位（基本は startOf('day')）
 * - ISOTime: 時刻（同一日の Dayjs に紐づけて生成）
 */
export type ISODate = Dayjs;
export type ISOTime = Dayjs;

/**
 * 1つの配送時間枠
 * enabled が false の場合は選択不可として表示
 */
export interface SimpleTimeSlot {
  start: ISOTime; // 同日の HH:mm
  end: ISOTime;   // 同日の HH:mm
  enabled: boolean;
}

/**
 * 1日の時間枠の情報を持つデータ
 */
export interface SimpleDaySchedule {
  date: ISODate;        // startOf('day')
  slots: SimpleTimeSlot[];
}

/**
 * 期間のスケジュール配列
 */
export type SimpleSchedule = SimpleDaySchedule[];

/* ===== スタブデータ作成ユーティリティ ===== */

/**
 * 画像サンプルに合わせた 1時間枠のテンプレート
 */
const BASE_SIMPLE_SLOTS: ReadonlyArray<Readonly<[string, string]>> = [
  ['08:05', '09:25'],
  ['10:10', '11:30'],
  ['12:15', '13:20'],
  ['13:30', '14:50'],
  ['15:20', '16:40']
] as const;

/**
 * UI検討用のスタブスケジュールを生成
 * - days: 生成日数（今日を含む）
 * - disablePattern: 日ごとの無効化インデックスを指定。
 *   キーは以下のいずれかをサポートします。
 *   - 'YYYY-MM-DD'（日付文字列）
 *   - 相対日インデックスの文字列（'0','1',...）: today からのオフセット
 */
export function generateStubSimpleSchedule(
  days = 7,
  disablePattern: Record<string, number[] | 'all'> = {}
): SimpleSchedule {
  const today = dayjs().startOf('day');
  const schedules: SimpleSchedule = [];

  //指定された日数分、指定されたパターンに基づきスケジュール情報を生成
  for (let i = 0; i < days; i++) {
    //ループ回数に応じ何日目のスケジュール情報を生成するか指定
    const date = today.add(i, 'day');
    const dateKey = date.format('YYYY-MM-DD');

    /*
    キーの有効・無効値の指定はまず日付キーのレコードを検索する。
    なければRecordのキーの相対インデックス（'0','1',...）を参照
    */
    // TODO: 当日の場合、過去時間帯は disabled にする
    const disabled =
      disablePattern[dateKey] ?? disablePattern[String(i)];

    const slots: SimpleTimeSlot[] = BASE_SIMPLE_SLOTS.map(([start, end], idx) => {
      const startDt = dayjs(`${dateKey}T${start}`); // ISO 8601 で安全にパース
      const endDt = dayjs(`${dateKey}T${end}`);
      /*
       すべて無効化する（all指定）のであればすべてfalse。
       配列がキーから取得できれば、配列の指定インデックスに基づき有効/無効化を選択
       指定がなければ簡易的にランダムに有効/無効のスケジュールを配置
       */
      const enabled =
        disabled === 'all'
          ? false
          : Array.isArray(disabled)
          ? !disabled.includes(idx)
          : // 適度に無効枠を混ぜる簡易ルール（指定がなければ）
            !((i % 3 === 0 && idx === 1) || (i % 3 === 2 && idx >= 3));

      return { start: startDt, end: endDt, enabled };
    });

    schedules.push({ date, slots });
  }

  return schedules;
}

/**
 * そのまま使えるデフォルトのスタブデータ（7日分）
 */
export const STUB_SIMPLE_SCHEDULE: SimpleSchedule = generateStubSimpleSchedule(7);

export const MEDICAL_STUB_SIMPLE_SCHEDULE: SimpleSchedule = generateStubSimpleSchedule(7, {
  // today からの相対日インデックスで指定（0..6）
  '0': [0,1,3,4],
  '1': [0,1,3,4],
  '2': [0,1,3,4],
  '3': [0,1,3,4],
  '4': [0,1,3,4],
  '5': [0,1,3,4],
  '6': [0,1,3,4]
});

export const searchSimpleSchedule = (
  date: ISODate, startTime: ISOTime, endTime: ISOTime
): { slot: SimpleTimeSlot, index: number } | undefined => {
    const daySchedule = MEDICAL_STUB_SIMPLE_SCHEDULE.find(schedule =>
      schedule.date.isSame(date, 'day')
    );

    if (!daySchedule) {
      return undefined;
    }

    const slotIndex = daySchedule.slots.findIndex(slot =>
      slot.start.isSame(startTime, 'minute')  && slot.end.isSame(endTime, 'minute') && slot.enabled
    );

    if (slotIndex === -1) {
      return undefined;
    }

    return { slot: daySchedule.slots[slotIndex], index: slotIndex };
};